import { useCallback } from "react";
import { toast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import type { PlainRunner } from "@/queries/runner-queries";
import { useCreateRunnerLogsToken } from "@/queries/runner-queries";

export const useRunnerActions = (runner?: PlainRunner) => {
    const { data: logsToken } = useCreateRunnerLogsToken(runner?.runnerId);

    const downloadSupportBundle = useCallback(async () => {
        if (!runner?.runnerId) {
            return;
        }

        const supportBundleUrl = runner?.status?.supportBundleUrl;

        if (!supportBundleUrl || !logsToken) {
            return;
        }

        try {
            const supportBundleResponse = await fetch(supportBundleUrl, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${logsToken}`,
                },
            });

            if (!supportBundleResponse.ok) {
                throw new Error(
                    `Failed to download runner support bundle: ${supportBundleResponse.status} ${supportBundleResponse.statusText}`,
                );
            }

            const supportBundleBlob = await supportBundleResponse.blob();
            const url = window.URL.createObjectURL(supportBundleBlob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `runner-support-bundle-${runner.runnerId}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast({ title: "Support bundle download started" });
        } catch (error) {
            toast({
                title: "Failed to download support bundle",
                description: formatError(error),
            });
        }
    }, [runner?.status?.supportBundleUrl, logsToken, runner?.runnerId]);

    const isSupportBundleAvailable = !!runner?.status?.supportBundleUrl && !!logsToken;

    return {
        downloadSupportBundle,
        isSupportBundleAvailable,
    };
};
